import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Layout, Menu, theme, Grid, Button, MenuProps, Flex, Modal, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// services
import authService from 'core/services/auth';
import { isRTL } from 'core/services/language';

//components
import Icon from 'utils/icon';
import LanguageButton from 'components/language-button/language-button';
import PageName from 'components/pageName/pageName';
import PlanComponent from 'components/plan/plan';

// styles
import logo from 'assets/images/logo-dark.svg';
import styles from '../layout.module.scss';

const { useBreakpoint } = Grid;

function BusinessDashboardLayout() {
	const screens = useBreakpoint();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();

	//global states
	const language = useSelector((state: any) => state.language);

	// local states
	const [current, setCurrent] = useState('splash-screen');
	const [collapsed, setCollapsed] = useState(true);
	const [showAccountInfo, setShowAccountInfo] = useState(false);
	const [planOpen, setPlanOpen] = useState(false);
	const [notificationOpen, setNotificationsOpen] = useState(false);
	const {
		token: { colorBgContainer },
	} = theme.useToken();

	const menuItems1 = [
		{
			module: 'create-mortgage',
			key: 'mortgages/create',
			label: t('SIDEBAR.MORTGAGE_REQUEST'),
			icon: (
				<span>
					<Icon name="request" />
				</span>
			),
		},
		{
			module: 'mortgage-list',
			key: 'mortgages',
			label: t('SIDEBAR.MORTGAGE_LIST'),
			icon: (
				<span>
					<Icon name="companies" />
				</span>
			),
		},
	];

	const menuItems2 = [
		{
			module: 'profile',
			key: 'profile',
			label: t('SIDEBAR.PROFILE'),
			icon: (
				<span>
					<Icon name="profile" />
				</span>
			),
		},
		{
			module: 'users-management',
			key: 'users-management',
			label: t('SIDEBAR.USERS_MANAGEMENT'),
			icon: (
				<span>
					<Icon name="users" />
				</span>
			),
		},
	];

	// styles
	const siderStyle: React.CSSProperties = {
		overflowY: 'auto',
		overflowX: 'hidden',
		position: screens.sm ? 'sticky' : 'fixed',
		insetInlineStart: 0,
		top: 0,
		bottom: 0,
		scrollbarWidth: 'thin',
		zIndex: 99,
		background: colorBgContainer,
		height: !screens.sm ? 'unset' : '100vh',
	};

	const headerStyle: React.CSSProperties = {
		padding: '0px',
		background: colorBgContainer,
		position: 'sticky',
		top: 0,
		borderBottom: '1px solid #eee',
		paddingInline: '.9rem',
		zIndex: 100,
	};

	useEffect(() => {
		const paths = location.pathname.split('/');
		setCurrent(paths[2]);
	}, []);

	useEffect(() => {
		if (!screens.sm && !collapsed) document.body.style.overflowY = 'hidden';
		if (!screens.sm && collapsed) document.body.style.overflowY = 'auto';
	}, [collapsed]);

	// handlers
	const onChangeCollapsible = () => {
		setCollapsed(!collapsed);
	};

	const onMenuClick: MenuProps['onClick'] = (e: any) => {
		if (window.innerWidth <= 576) setCollapsed(true);

		setCurrent(e.key);
		navigate(e.key);
	};

	const showAccountHandler = () => {
		if (collapsed) {
			setShowAccountInfo(!showAccountInfo);
		}
	};

	const onChoosePlan = (type: 'free' | 'paid') => {};

	const onWalletClick = () => {
		if (window.innerWidth <= 576) setCollapsed(true);
		setCurrent('wallet');
		navigate('wallet');
	};

	const onLogout = () => {
		authService.clearAuthData();
		navigate('/');
	};

	return (
		<>
			<Layout hasSider style={{ minHeight: '100vh' }}>
				{!screens.sm && !collapsed && <div className={styles.siderBack} onClick={onChangeCollapsible}></div>}

				<Layout.Sider
					style={siderStyle}
					breakpoint="lg"
					collapsedWidth={screens.md && !collapsed ? 300 : screens.sm ? 70 : 0}
					width={300}
					collapsed={collapsed}
					onCollapse={(value) => setCollapsed(value)}
					trigger={null}>
					{screens.lg && (
						<Button
							size="small"
							color="default"
							variant="filled"
							className={styles.collapseBtn}
							onClick={onChangeCollapsible}
							style={{ insetInlineStart: collapsed ? 53 : 283, transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}>
							<Icon name="leftArrow" color="#2884EF" style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }} />
						</Button>
					)}

					<div>
						<img
							src={logo}
							alt="logo"
							style={{
								width: 140,
								margin: 20,
								marginInlineStart: 30,
								transition: 'all .2s ease-in-out',
								transform: `translateX(${
									screens.sm && collapsed && language === 'en' ? '-107px' : screens.sm && collapsed && language === 'ar' ? '23px' : '0px'
								})`,
							}}
						/>

						<div>
							<div>
								<h6
									className="mt-3 d-sm-none d-lg-block text-light"
									style={{ paddingInlineStart: 20, minHeight: 20, whiteSpace: 'nowrap' }}>
									{!collapsed && t('SIDEBAR.MAIN_MENU')}
								</h6>
								<Menu items={menuItems1} onClick={onMenuClick} mode="inline" selectedKeys={[current]} />
							</div>
							<hr style={{ marginInline: 20, borderColor: '#aaa' }} />
							<div>
								<h6
									className="pt-3 d-sm-none d-lg-block text-light "
									style={{ paddingInlineStart: 20, minHeight: 36, whiteSpace: 'nowrap' }}>
									{!collapsed && t('SIDEBAR.PERSONAL_INFORMATION')}
								</h6>
								<Menu items={menuItems2} onClick={onMenuClick} selectedKeys={[current]} />
							</div>
						</div>
					</div>

					<Flex gap={10} align="center" justify="space-between" className={styles.account}>
						<Flex align="center" gap={10} style={{ minWidth: 0 }}>
							<Button type="link" className="p-0" onMouseEnter={showAccountHandler} onMouseLeave={() => setShowAccountInfo(false)}>
								{/* <img src={sample1} /> */}
								{showAccountInfo && (
									<Flex gap={20} align="center" className={`${styles.collapsedAccountInfo} ${styles[language]}`}>
										<div>
											<h6 className="fw-semibold">Moemen Trade</h6>
											<span style={{ direction: 'ltr' }}>moemen.said@gmail.com</span>
										</div>
										<Button type="link" className="p-0" onClick={onLogout}>
											<Icon
												name="logout"
												width={30}
												height={30}
												color="var(--primary)"
												style={{ transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}
											/>
										</Button>
									</Flex>
								)}
							</Button>
							{!collapsed && (
								<div className={styles.account_data}>
									<h6 className="fw-semibold">Moemen Trade</h6>
									<span style={{ direction: 'ltr' }}>moemen.said@gmail.com</span>
								</div>
							)}
						</Flex>
						{!collapsed && (
							<Button type="link" className="p-0" onClick={() => navigate('/')}>
								<Icon
									name="logout"
									width={30}
									height={30}
									color="var(--primary)"
									style={{ transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}
								/>
							</Button>
						)}
					</Flex>
				</Layout.Sider>

				<Layout style={{ background: colorBgContainer }}>
					<Layout.Header style={headerStyle} className={styles.navHeader}>
						<img src={logo} alt="logo" className="d-sm-none" style={{ width: 120 }} />

						<Flex align="center" gap={10}>
							<Flex align="center" gap={10}>
								<Button
									color="default"
									variant="filled"
									className={`${styles.notificationBtn} order-1 order-md-0`}
									onClick={() => setNotificationsOpen(true)}>
									<Icon name="notifications" width={14} height={18} />
								</Button>

								<div className="order-0 order-md-1">
									<LanguageButton />
								</div>
							</Flex>

							<div style={{ transform: 'rotate(180deg)' }}>
								<Button type="link" className={`${styles.menuToggler} ${!collapsed && styles.active}`} onClick={onChangeCollapsible}>
									<span></span>
									<span></span>
									<span></span>
								</Button>
							</div>
						</Flex>
					</Layout.Header>

					<Layout.Content className={styles.content}>
						<div>
							<h4 className="fw-semibold">{t('COMMON.WELCOME')}, moemen</h4>
							<PageName />
						</div>

						<Outlet />
					</Layout.Content>
				</Layout>
			</Layout>

			<Drawer
				open={notificationOpen}
				closeIcon={null}
				onClose={() => setNotificationsOpen(false)}
				placement={isRTL() ? 'left' : 'right'}
				className="notification-drawer"
				width={400}
				title={
					<Flex justify="space-between" align="flex-start">
						<div>
							<h4 className="mb-0">{t('COMMON.NOTIFICATIONS')}</h4>
							<Button type="link" className="p-0 h-auto fw-semibold" size="middle">
								{t('COMMON.MARK_ALL_AS_READ')}
							</Button>
						</div>
						<Button type="link" className="p-0" onClick={() => setNotificationsOpen(false)}>
							<Icon name="close" width={15} height={15} color="#555" />
						</Button>
					</Flex>
				}>
				<p className="text-center text-light mt-4">{t('COMMON.NO_DATA')}</p>
			</Drawer>
		</>
	);
}

export default BusinessDashboardLayout;
